import React, { useMemo } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Button, Container, Link, Typography, CssBaseline } from '@mui/material';

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { ThemeProvider as CustomThemeProvider, useTheme } from "../contexts/ThemeContext";
import { useSystemButtonStyles } from "../hooks/useSystemButtonStyles";
import AuthPageToggleBar from "../components/AuthPageToggleBar";
import {
  authCardShellSx,
  authLogoSx,
  authPageOuterSx,
} from "../utils/authPageStyles";
import { APP_FONT_FAMILY } from "../constants/typography";
import { getMm, getPageTitleSx } from "../theme/modernMaterialEnterprise";

const FONT = APP_FONT_FAMILY;
const LAST_UPDATED = "2026-08-18";
const SUPPORT_EMAIL = "support@neweracss.com";

function DataDeletionContent() {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const { outlineButtonSx } = useSystemButtonStyles();
  const cardShellSx = useMemo(() => authCardShellSx(isDarkMode), [isDarkMode]);
  const logoSx = useMemo(() => authLogoSx(isDarkMode), [isDarkMode]);

  const headingSx = {
    fontFamily: FONT,
    fontWeight: 700,
    fontSize: { xs: "1.05rem", sm: "1.125rem" },
    color: getMm(isDarkMode).onSurface,
    mt: 3,
    mb: 1,
  };

  const bodySx = {
    fontFamily: FONT,
    fontSize: "0.875rem",
    lineHeight: 1.65,
    color: getMm(isDarkMode).onSurfaceVariant,
    whiteSpace: "pre-line",
  };

  const listSx = {
    ...bodySx,
    pl: 2.5,
    my: 1,
    "& li": { mb: 0.75 },
  };

  const stepListSx = {
    ...listSx,
    counterReset: "step",
    listStyle: "none",
    pl: 0,
    "& li": {
      ...listSx["& li"],
      position: "relative",
      pl: 3,
      mb: 1.25,
    },
    "& li::before": {
      counterIncrement: "step",
      content: 'counter(step) "."',
      position: "absolute",
      left: 0,
      fontWeight: 800,
      color: isDarkMode ? "#86EFAC" : "#388E3C",
    },
  };

  const requestSteps = t("data_deletion_request_steps", { returnObjects: true }) || [];
  const deletedItems = t("data_deletion_deleted_items", { returnObjects: true }) || [];
  const keptItems = t("data_deletion_kept_items", { returnObjects: true }) || [];

  return (
    <Box sx={{ ...authPageOuterSx(isDarkMode), minHeight: "100vh" }}>
      <CssBaseline />
      <AuthPageToggleBar />
      <Container maxWidth="md" sx={{ py: { xs: 3, sm: 5 }, px: { xs: 2, sm: 3 } }}>
        <Box sx={{ textAlign: "center", mb: 3 }}>
          <Box
            component="img"
            src={isDarkMode ? "NewEraLogo.png" : "NewEraBlackLogo.png"}
            alt="NewEra CSS"
            sx={{ ...logoSx, maxWidth: { xs: 200, sm: 260 }, mb: 2 }}
          />
          <Typography
            component="h1"
            sx={{ ...getPageTitleSx(isDarkMode), mb: 0.75, textAlign: "center" }}
          >
            {t("data_deletion_page_title")}
          </Typography>
          <Typography sx={{ ...bodySx, textAlign: "center" }}>
            {t("data_deletion_page_subtitle")}
          </Typography>
          <Typography
            sx={{
              fontFamily: FONT,
              fontSize: "0.8125rem",
              color: getMm(isDarkMode).onSurfaceVariant,
              mt: 1,
            }}
          >
            {t("data_deletion_page_last_updated", { date: LAST_UPDATED })}
          </Typography>
        </Box>

        <Box sx={{ ...cardShellSx, p: { xs: 2.5, sm: 4 }, mb: 3 }}>
          <Typography sx={bodySx}>{t("data_deletion_intro")}</Typography>

          <Typography component="h2" sx={{ ...headingSx, mt: 3 }}>
            {t("data_deletion_section_request_title")}
          </Typography>
          <Typography sx={{ ...bodySx, mb: 1.5 }}>{t("data_deletion_section_request_body")}</Typography>
          <Box component="ol" sx={stepListSx}>
            {requestSteps.map((step) => (
              <Box component="li" key={step}>
                {step}
              </Box>
            ))}
          </Box>

          <Typography component="h2" sx={headingSx}>
            {t("data_deletion_section_deleted_title")}
          </Typography>
          <Typography sx={bodySx}>{t("data_deletion_section_deleted_body")}</Typography>
          <Box component="ul" sx={listSx}>
            {deletedItems.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </Box>

          <Typography component="h2" sx={headingSx}>
            {t("data_deletion_section_kept_title")}
          </Typography>
          <Typography sx={bodySx}>{t("data_deletion_section_kept_body")}</Typography>
          <Box component="ul" sx={listSx}>
            {keptItems.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </Box>

          <Typography component="h2" sx={headingSx}>
            {t("data_deletion_section_retention_title")}
          </Typography>
          <Typography sx={bodySx}>{t("data_deletion_section_retention_body")}</Typography>

          <Typography component="h2" sx={headingSx}>
            {t("data_deletion_section_contact_title")}
          </Typography>
          <Typography sx={bodySx}>
            {t("data_deletion_section_contact_body")}
            {"\n\n"}
            <Link href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(t("data_deletion_email_subject"))}`} sx={{ color: isDarkMode ? "#86EFAC" : "#388E3C" }}>
              {SUPPORT_EMAIL}
            </Link>
          </Typography>

          <Typography sx={{ ...bodySx, mt: 3, fontSize: "0.8125rem", color: getMm(isDarkMode).onSurfaceVariant }}>
            {t("data_deletion_privacy_link_prefix")}{" "}
            <Link component={RouterLink} to="/privacy" sx={{ color: isDarkMode ? "#86EFAC" : "#388E3C" }}>
              {t("login_privacy_policy")}
            </Link>
          </Typography>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "center", gap: 2, flexWrap: "wrap" }}>
          <Button
            component={RouterLink}
            to="/login"
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            sx={{ ...outlineButtonSx, textTransform: "none" }}
          >
            {t("privacy_back_to_login")}
          </Button>
        </Box>
      </Container>
    </Box>
  );
}

export default function DataDeletionPage() {
  return (
    <CustomThemeProvider>
      <DataDeletionContent />
    </CustomThemeProvider>
  );
}
